import React, { useMemo } from 'react'
import { connect, ConnectedProps } from 'react-redux'

import { MultiSelectOption } from 'controlled-components'
import {
  addBottomSelectedItem,
  filterOptionsBySearch,
  removeBottomSelectedItem,
  selectBottomData,
  selectBottomSearchTerm,
  selectBottomSelectedItems,
} from 'slices/aiSegmentBuilder/picker'

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

interface OwnProps {
  pickerId: number
}

interface DataItem {
  id: string
  name: string
}

type Props = OwnProps & ConnectedProps<typeof connector>

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Sub-container for displaying paginated search results.
 * Renders MultiSelectOption for each item in the current page.
 */
const OptionsListContainer: React.FC<Props> = ({
  pickerId,
  data,
  searchTerm,
  selectedItems,
  addSelectedItem,
  removeSelectedItem,
}) => {
  // Derived: visible options. Filtered client-side via the shared helper so
  // this list and the header's "select all on page" always mean the same set.
  const options: DataItem[] = useMemo(
    () => filterOptionsBySearch(data, searchTerm),
    [data, searchTerm]
  )

  // Derived: selected items set for efficient lookup
  const selectedItemsSet = useMemo(
    () => new Set(selectedItems),
    [selectedItems]
  )

  // Handler
  const handleOptionSelect = (key: string) => {
    if (selectedItemsSet.has(key)) {
      removeSelectedItem({ pickerId, key })
    } else {
      addSelectedItem({ pickerId, key })
    }
  }

  return (
    <>
      {options.map(item => (
        <MultiSelectOption
          key={item.id}
          value={item.id}
          selected={selectedItemsSet.has(item.id)}
          onSelect={handleOptionSelect}
        >
          {item.name}
        </MultiSelectOption>
      ))}
    </>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// Redux Connection
// ═══════════════════════════════════════════════════════════════════════════

const mapStateToProps = (state: any, ownProps: OwnProps) => ({
  data: selectBottomData(state, ownProps.pickerId),
  searchTerm: selectBottomSearchTerm(state, ownProps.pickerId),
  selectedItems: selectBottomSelectedItems(state, ownProps.pickerId),
})

const mapDispatchToProps = {
  addSelectedItem: addBottomSelectedItem,
  removeSelectedItem: removeBottomSelectedItem,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

export default connector(OptionsListContainer)
